import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Package } from "lucide-react";
import { ZoomableImage } from "../components/ZoomableImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter 178i: public read-side for a customer-scoped quote link. Same
// visual layout as ProductShareLinkPage but customer_name row is hidden
// and prices are in the customer's default currency (already computed
// server-side using Settings coefficient).
export default function CustomerQuoteLinkPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API}/public/customer-quote/${token}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const s = e?.response?.status;
        if (s === 404) setError("This quote link doesn't exist.");
        else if (s === 410) setError("This quote link has expired.");
        else setError("Something went wrong loading this quote link.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><p className="text-sm text-gray-400">Loading…</p></div>;
  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6" data-testid="customer-quote-error">
      <div className="max-w-md text-center">
        <Package size={40} strokeWidth={1.5} className="mx-auto text-gray-300 mb-3" />
        <h1 className="text-lg font-semibold text-gray-800 mb-1">Link Unavailable</h1>
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    </div>
  );

  const products = data?.products || [];
  const currency = data?.currency || "USD";
  const symbol = currency === "CAD" ? "C$" : currency === "CNY" ? "¥" : "$";
  const expiresLabel = data?.expires_at ? new Date(data.expires_at).toLocaleDateString() : "-";

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-quote-page">
      <header className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand-600 text-white flex items-center justify-center">
              <Package size={16} strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900">Product Quotation</h1>
              <p className="text-[11px] text-gray-500">{products.length} item(s) · prices in {currency} · link expires {expiresLabel}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {products.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl py-24 text-center"><p className="text-sm text-gray-400">No products in this quote.</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {products.map((p) => (
              <div key={p.product_id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="aspect-[4/3] bg-gray-50">
                  <ZoomableImage src={p.photo_url} alt={p.description} className="w-full h-full object-cover" previewSize={340} />
                </div>
                <div className="p-3">
                  <h3 className="font-mono text-sm font-bold text-gray-900 truncate" title={p.entries?.[0]?.item_no}>{p.entries?.[0]?.item_no || "-"}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2" title={p.description}>{p.description}</p>
                  {p.contract_no && (
                    <p className="font-mono text-[11px] text-gray-400 mt-1.5">Contract {p.contract_no}</p>
                  )}
                  <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                    {(p.entries || []).map((e) => (
                      <div key={e.entry_id} className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">{e.size || "-"}{e.qty ? ` · ${e.qty} pcs` : ""}</span>
                        <span className="font-mono font-semibold text-gray-900">{e.price != null ? `${symbol}${Number(e.price).toFixed(2)}` : "-"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
